import { useMoveDragTeardown } from "@/domains/documents/hooks/useMoveDragTeardown";
import { useStaleAnchorEviction } from "@/domains/documents/hooks/useVersionExpansion";
import { isSystemFolder } from "@/domains/documents/services/folderService";
import type { TreeFolder } from "@/domains/documents/types";
import { isBriefFolder } from "@/domains/documents/utils/briefFolder";
import {
  isEligibleMoveDropTarget,
  writeMoveDragPayload,
  type MoveDragItem,
} from "@/domains/documents/utils/documentMoveDrag";
import { useCallback } from "react";
import type { DocumentWithTags } from "./documentTableTypes";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTableDocumentKinds } from "./useTableDocumentKinds";
import type { useTableMoveState } from "./useTableMoveState";
type Options = Pick<
  ReturnType<typeof useTableMoveState>,
  | "pressedSelfHandledPart"
  | "moveTargetFolders"
  | "updateActiveMove"
  | "activeMove"
  | "dragSourceKey"
  | "folderDragDrop"
> &
  Pick<
    ReturnType<typeof documentTableOptions>,
    "store" | "selectedFolderIds" | "onMoveFolder" | "onDropDocument" | "isRestore"
  > &
  Pick<ReturnType<typeof useTableBasics>, "t" | "folderRename" | "documentRename"> &
  Pick<ReturnType<typeof useTableDialogState>, "versionExpansion"> &
  Pick<ReturnType<typeof useTableDocumentKinds>, "displayDocuments">;
export function useTableMoveActions(options: Options) {
  const {
    moveTargetFolders,
    activeMove,
    onMoveFolder,
    folderRename,
    dragSourceKey,
    folderDragDrop,
    onDropDocument,
    documentRename,
    isRestore,
    versionExpansion,
    displayDocuments,
  } = options;
  const { startMoveDrag, endMoveDrag } = useTableDragLifecycle(options);

  const folderMoveProps = useCallback(
    (folder: TreeFolder) => {
      // A row being renamed must not be draggable: the rename `<input>` lives
      // inside the same row div, and a draggable ancestor turns a text-selection
      // drag into a row drag the input never recovers from.
      const canDrag =
        !!onMoveFolder &&
        !folder.isContainer &&
        !isSystemFolder(folder) &&
        !isBriefFolder(folder) &&
        !folderRename.isEditing(folder.id);
      const isDropAvailable =
        activeMove !== null &&
        isEligibleMoveDropTarget(moveTargetFolders, folder.id, activeMove.folderIds);
      return {
        isDraggable: canDrag,
        isDragging: dragSourceKey === `folder:${folder.id}`,
        isDropAvailable,
        isDragOver: folderDragDrop.isDraggedOver(folder.id),
        onDragStart: canDrag
          ? startMoveDrag({ kind: "folder", id: folder.id, label: folder.name })
          : undefined,
        onDragEnd: canDrag ? endMoveDrag : undefined,
        // No `hasChildren`: this view lists folders flat and wires no
        // hover-expand, so there is nothing for a dwell to reveal — you open a
        // folder by clicking into it. Auto-expand belongs to the nested tree.
        onDragEnter: folderDragDrop.handleDragEnter(folder.id),
        onDragOver: folderDragDrop.handleDragOver(folder.id),
        onDragLeave: folderDragDrop.handleDragLeave,
        onDrop: folderDragDrop.handleDrop(folder.id),
      };
    },
    [
      activeMove,
      dragSourceKey,
      endMoveDrag,
      folderDragDrop,
      folderRename,
      moveTargetFolders,
      onMoveFolder,
      startMoveDrag,
    ]
  );

  const documentMoveProps = useCallback(
    (doc: DocumentWithTags, isExpandedVersion: boolean) => {
      const canDrag =
        !!onDropDocument && !isExpandedVersion && !documentRename.isEditing(doc.id) && !isRestore;
      return {
        isDraggable: canDrag,
        isDragging: dragSourceKey === `document:${doc.id}`,
        onDragStart: canDrag
          ? startMoveDrag({ kind: "document", id: doc.id, label: doc.filename })
          : undefined,
        onDragEnd: canDrag ? endMoveDrag : undefined,
      };
    },
    [documentRename, dragSourceKey, endMoveDrag, isRestore, onDropDocument, startMoveDrag]
  );

  // A new upload can flip v2→v3 as the anchor, leaving a cached chain that was
  // fetched with the old anchor excluded. Drop those so the next open refetches.
  useStaleAnchorEviction(versionExpansion, displayDocuments);
  return { folderMoveProps, startMoveDrag, endMoveDrag, documentMoveProps };
}

function useTableDragLifecycle(options: Options) {
  const {
    pressedSelfHandledPart,
    moveTargetFolders,
    store,
    selectedFolderIds,
    t,
    updateActiveMove,
    activeMove,
  } = options;
  const startMoveDrag = useCallback(
    (item: MoveDragItem) => (event: React.DragEvent<HTMLDivElement>) => {
      // `event.target` here is the row root, whatever was pressed — only the
      // recorded pointer-down origin can tell a row drag from a press on the
      // checkbox or the actions cell.
      if (pressedSelfHandledPart.current) {
        event.preventDefault();
        return;
      }

      const payload = writeMoveDragPayload(
        event.dataTransfer,
        moveTargetFolders,
        item,
        {
          docIds: store?.selectedDocumentIds ?? [],
          folderIds: selectedFolderIds,
        },
        (count) => t.documents.folder.draggingItems.replace("{count}", String(count))
      );
      updateActiveMove(payload, `${item.kind}:${item.id}`);
    },
    [
      moveTargetFolders,
      pressedSelfHandledPart,
      selectedFolderIds,
      store,
      t.documents.folder.draggingItems,
      updateActiveMove,
    ]
  );

  const endMoveDrag = useCallback(() => updateActiveMove(null), [updateActiveMove]);

  // Rows here are virtualised, so the source row of a drag can be unmounted
  // mid-drag and its `onDragEnd` never fires. Watch the document too.
  useMoveDragTeardown(activeMove !== null, endMoveDrag);

  return { startMoveDrag, endMoveDrag };
}
